export const formatMoney = (value: string) => {
  if (!value || value === ".00") return "";
  value = value.replace(/[^\d.]/g, "");
  const [intPart, decPart = ""] = value.split(".");
  const decimals = (decPart + "00").slice(0, 2);
  return `${intPart || "0"}.${decimals}`;
};

export type amountFormatterProps = (
  amount: number,
  currency?: string,
  numberFormatOptions?: {
    country?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
) => string;

/** Decimal amounts always use `en-US` separators, independent of UI language. */
export const amountFormatter: amountFormatterProps = (
  amount,
  currency = "USD",
  {
    country = "en-US",
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = {}
) =>
  new Intl.NumberFormat(country, {
    style: "decimal",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);

/** Decimal amount + ISO code; USD also includes `$`. */
export function formatLabeledAmount(
  amount: number,
  currency = "USD",
  numberFormatOptions?: {
    country?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  const formatted = amountFormatter(amount, currency, numberFormatOptions);
  const code = currency.toUpperCase();
  return code === "USD" ? `$${formatted} ${code}` : `${formatted} ${code}`;
}

/** Already-formatted decimal plus currency. USD is `$` and the code; others are the code only. */
export function formatAmountWithCode(amount: string, currency: string): string {
  const code = currency.trim().toUpperCase();
  if (!code) return amount;
  return code === "USD" ? `$${amount} ${code}` : `${amount} ${code}`;
}

/** Hero typography: en-US `25.00` split; `$` + ISO for USD, ISO suffix otherwise. */
export function formatHeroAmount(amount: number, currency: string) {
  const formatted = amountFormatter(amount, currency, {
    country: "en-US",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const [intPart, decPart = "00"] = formatted.split(".");
  const code = currency.toUpperCase();
  return {
    prefix: code === "USD" ? "$" : "",
    suffix: ` ${code}`,
    intPart,
    decPart,
  };
}

export const getCurrentDateFormatted = (): string => {
  const date = new Date();

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};
